import { useEffect, useState } from "react";
import {
  createEmailConfig,
  getEmailConfigList,
  updateEmailConfig,
  viewConfigDetails,
} from "../../services/EmailService.js/emailService";
import Loader from "../../components/Loader/Loader";
import { Check, Edit, InfoIcon, Plus } from "lucide-react";

export default function EmailConfiguration() {
  const [driver, setDriver] = useState("smtp");
  const [active, setActive] = useState(true);
  const [host, setHost] = useState(null);
  const [port, setPort] = useState(null);
  const [username, setUsername] = useState(null);
  const [password, setPassword] = useState(null);
  const [encryption, setEncryption] = useState(null);
  const [fromEmail, setFromEmail] = useState(null);
  const [fromName, setFromName] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const [configList, setConfigList] = useState(null);
  const [formType, setFormtype] = useState("create");
  const [updateResponse, setUpdateResponse] = useState(null);
  const [viewResponse, setViewResponse] = useState(null);
  const [id, setId] = useState(null);  

  const [configResponse, setConfigResponse] = useState(null);

  const handleResetForm = () => {
    setFormtype("create");
    setDriver("smtp");
    setActive(true);
    setHost("");
    setPort("");
    setUsername("");
    setPassword("");
    setEncryption("");
    setFromEmail("");
    setFromName("");
    setIsLoading(false);
    setConfigResponse(null);
    setViewResponse(null);
    setUpdateResponse(null);
  };

  const handlecreate = async (e) => {
    e.preventDefault();
    if (!driver) return;

    const rawPayload = {
      driver,
      is_active: active,
      host: host,
      port: port,
      username,
      password,
      encryption,
      from_email: fromEmail,
      from_name: fromName,
    };

    const payload = Object.fromEntries(
      Object.entries(rawPayload).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
    setIsLoading(true);

    try {
      const res = await createEmailConfig(payload);

      if (res) {
        setConfigResponse(res);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfigList = async () => {
    try {
      const res = await getEmailConfigList();

      if (res) {
        setConfigList(res);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getConfigList();
  }, []);

  const handleView = async (id) => {
    setFormtype("update");
    setConfigResponse(null);
    try {
      const res = await viewConfigDetails({ id });

      if (res?.data) {
        setViewResponse(res);

        setId(res?.data?.id);
        setDriver(res?.data?.driver);
        setHost(res?.data?.host);
        setPort(res?.data?.port);
        setEncryption(res?.data?.encryption);
        setFromEmail(res?.data?.from_email);
        setFromName(res?.data?.from_name);
        setActive(res?.data?.is_active);
        setUsername(res?.data?.username);
        setPassword(res?.data?.password);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!driver || !id) return;

    const rawPayload = {
      id,
      driver,
      is_active: active,
      host: host,
      port: port,
      username,
      password,
      encryption,
      from_email: fromEmail,
      from_name: fromName,
    };

    const payload = Object.fromEntries(
      Object.entries(rawPayload).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
    setIsLoading(true);

    try {
      const res = await updateEmailConfig(payload);

      if (res) {
        setUpdateResponse(res);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 p-6 my-6">
      {isLoading && <Loader />}
      <div className="max-w-7xl mx-auto space-y-2">
        {/* Header */}
        <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 p-6 text-white shadow-lg">
          <h1 className="text-2xl font-semibold">Email Configuration</h1>
          <p className="text-sm opacity-90">
            {formType === "create"
              ? " Configure email drivers, SMTP credentials & service status"
              : "Update email drivers, SMTP credentials & service status"}
          </p>
        </div>

        {/* create config button */}
        {(configResponse?.status?.code === 200 ||
          updateResponse?.status?.code === 200) && (
          <button
            onClick={handleResetForm}
            className="rounded-xl w-fit hover:from-gray-800 hover:to-gray-900 active:from-slate-600  bg-gradient-to-r flex items-center gap-2 from-indigo-600 via-blue-600 to-cyan-500 p-4 text-white shadow-lg"
          >
            <Plus />
            Create New
          </button>
        )}

        {/* show response message */}

        {(configResponse?.status?.code === 200 ||
          updateResponse?.status?.code === 200) && (
          <div className="flex items-center gap-4 px-6 py-4 rounded-2xl bg-green-50 border-2 border-green-300 text-green-800 shadow-md">
            <Check className="w-6 h-6 text-green-700 animate-bounce" />
            <p className="text-base font-semibold">
              {configResponse?.status?.code === 200
                ? "Email Server configured successfully"
                : "Email Server updated successfully"}
            </p>
          </div>
        )}

        {configResponse && configResponse?.status?.code !== 200 && (
          <div className="flex items-center gap-4 px-6 py-4 rounded-2xl bg-red-50 border-2 border-red-300 text-red-800 shadow-md">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-red-200 text-red-700 font-bold text-sm">
              !
            </span>
            <p className="text-base font-semibold">Configuration failed</p>
          </div>
        )}

        {(configResponse?.status?.code !== 200 ||
          updateResponse?.status?.code !== 200) && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <form
              onSubmit={formType === "create" ? handlecreate : handleUpdate}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {formType === "update" && (
                  <div className="flex flex-col gap-1">
                    <label htmlFor="id">
                      Id
                    </label>
                    <input
                      type="text"
                      value={id}
                      onChange={(e) => setId(e.target.value)}
                      required
                      readOnly
                      placeholder="Id"
                      className="border rounded-lg w-fit px-4 py-2"
                    />
                  </div>
                )}

                {/* Email Driver */}
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Email Driver
                  </label>
                  <select
                    value={driver}
                    onChange={(e) => setDriver(e.target.value)}
                    className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  >
                    <option value="">Select Driver</option>
                    <option value="mailgun">Mailgun</option>
                    <option value="ses">Amazon SES</option>
                    <option value="smtp">SMTP</option>
                  </select>
                </div>

                {/* From Email */}
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    required
                    placeholder="noreply@company.com"
                    className="w-full border rounded-lg px-4 py-2"
                  />
                </div>

                {/* From Name */}
                <div className="flex flex-col gap-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    type="text"
                    required
                    placeholder="Company Support"
                    className="w-full border rounded-lg px-4 py-2"
                  />
                </div>
              </div>

              {/* server config Fields */}
              <div className="rounded-xl border bg-gradient-to-br from-slate-50 to-slate-100 p-5 space-y-4">
                <h3 className="text-sm font-semibold text-gray-700">
                  Driver Configuration
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-4  xl:grid-cols-5 gap-4">
                  <input
                    type="text"
                    value={host}
                    onChange={(e) => setHost(e.target.value)}
                    required
                    placeholder="Host"
                    className="border rounded-lg px-4 py-2"
                  />
                  <input
                    required
                    type="number"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    placeholder="Port"
                    className="border rounded-lg px-4 py-2"
                  />
                  <input
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    type="text"
                    placeholder="Username"
                    className="border rounded-lg px-4 py-2"
                  />
                  <input
                    required={formType === "create"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type="password"
                    placeholder="Password"
                    className="border rounded-lg px-4 py-2"
                  />
                  <select
                    value={encryption}
                    onChange={(e) => setEncryption(e.target.value)}
                    required
                    className="border rounded-lg px-4 py-2"
                  >
                    <option value="">Encryption</option>
                    <option value="tls">TLS</option>
                    <option value="ssl">SSL</option>
                    <option value="none">None</option>
                  </select>
                </div>
                {formType === "update" && (
                  <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-800">
                    <InfoIcon className="w-5 h-5 mt-0.5 text-blue-600" />
                    <p className="text-sm leading-relaxed">
                      <span className="font-semibold">Note:</span> Only Enter
                      the password if you want to change the existing one.
                      Otherwise, leave it Empty.
                    </p>
                  </div>
                )}
              </div>

              {/* Status Toggle */}
              <div className="flex items-center justify-between rounded-lg border bg-gradient-to-r from-slate-50 to-slate-100 p-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Email Service Status
                  </p>
                  <p className="text-xs text-gray-500">
                    Enable or disable email sending
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setActive(!active)}
                  className={`w-12 h-6 rounded-full relative transition ${
                    active ? "bg-emerald-500" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition ${
                      active ? "translate-x-6" : ""
                    }`}
                  />
                </button>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4">
                {formType === "create" && (
                  <button
                    onClick={handleResetForm}
                    type="button"
                    className="px-6 py-2 rounded-lg border text-gray-600 hover:bg-gray-100"
                  >
                    Reset
                  </button>
                )}
                <button
                  type="submit"
                  className="px-6 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow hover:opacity-90"
                >
                  {formType === "create"
                    ? " Save Configuration "
                    : " Update Configuration "}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Report Table */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Email Configuration Report
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full border rounded-lg overflow-hidden">
              <thead className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm">Id</th>
                  <th className="px-4 py-3 text-left text-sm">Driver</th>
                  <th className="px-4 py-3 text-left text-sm">Host</th>
                  <th className="px-4 py-3 text-left text-sm">Port</th>
                  <th className="px-4 py-3 text-left text-sm">Username</th>
                  <th className="px-4 py-3 text-center text-sm">Encryption</th>
                  <th className="px-4 py-3 text-center text-sm">From Email</th>
                  <th className="px-4 py-3 text-center text-sm">From Name</th>
                  <th className="px-4 py-3 text-center text-sm">Status</th>
                  <th className="px-4 py-3 text-center text-sm">Created at</th>
                  <th className="px-4 py-3 text-center text-sm">
                    <Edit />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {configList?.data?.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50 transition">
                    {/* Id */}
                    <td className="px-4 py-3 font-medium text-sm">{item.id}</td>

                    {/* Driver */}
                    <td className="px-4 py-3 text-sm capitalize">
                      {item.driver}
                    </td>

                    {/* Host */}
                    <td className="px-4 py-3 text-sm">{item.host}</td>

                    {/* Port */}
                    <td className="px-4 py-3 text-sm">{item.port}</td>

                    {/* Username */}
                    <td className="px-4 py-3 text-sm">{item.username}</td>

                    {/* Encryption */}
                    <td className="px-4 py-3 text-center">
                      <span className="px-3 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700 font-semibold uppercase">
                        {item.encryption}
                      </span>
                    </td>

                    {/* From Email */}
                    <td className="px-4 py-3 text-sm">{item.from_email}</td>

                    {/* From Name */}
                    <td className="px-4 py-3 text-sm">{item.from_name}</td>

                    {/* Status */}
                    <td className="px-4 py-3 text-center">
                      {item.is_active === 1 ? (
                        <span className="px-3 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                          Active
                        </span>
                      ) : (
                        <span className="px-3 py-1 text-xs rounded-full bg-red-100 text-red-700 font-semibold">
                          Inactive
                        </span>
                      )}
                    </td>

                    {/* Created At */}
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(item.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>

                    <td className="px-4 py-3 text-sm">
                      <button
                        type="button"
                        onClick={() => handleView(item.id)}
                        className="rounded-xl hover:from-gray-800 hover:to-gray-900 active:from-slate-600 bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 p-2 text-white shadow-lg"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
